import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BellRing, Loader2 } from "lucide-react";

// Compact "Document reminders" preference popover for portal + external users (PROMPT 13E).
export function DocDigestSettings({ testid = "doc-digest-settings" }) {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/me/document-digest/preferences").then(({ data }) => setPrefs(data.preferences)).catch(() => {});
  useEffect(() => { if (open && !prefs) load(); }, [open]);

  const save = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    setSaving(true);
    try { const { data } = await api.put("/me/document-digest/preferences", patch); setPrefs(data.preferences); }
    catch (e) { toast.error(errMsg(e)); load(); }
    finally { setSaving(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 h-9 rounded-full border-[#D6E0E7] text-[#1C4358]" data-testid={`${testid}-trigger`}>
          <BellRing size={14} /> Reminders
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72" data-testid={`${testid}-panel`}>
        <div className="flex items-center gap-2 mb-3">
          <BellRing size={15} className="text-[#7C3AED]" />
          <span className="font-display font-semibold text-[14px] text-[#1C4358]">Document reminders</span>
          {saving && <Loader2 size={13} className="animate-spin text-muted-foreground ml-auto" />}
        </div>
        {!prefs ? (
          <div className="text-[13px] text-muted-foreground py-4 text-center">Loading…</div>
        ) : (
          <div className="space-y-3.5">
            <div className="flex items-center justify-between">
              <Label className="text-[13px]">Email me a digest</Label>
              <Switch checked={!!prefs.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid={`${testid}-enabled`} />
            </div>
            <div className={prefs.enabled ? "space-y-3.5" : "space-y-3.5 opacity-50 pointer-events-none"}>
              <div className="space-y-1.5">
                <Label className="text-[13px]">Frequency</Label>
                <Select value={prefs.frequency} onValueChange={(v) => save({ frequency: v })}>
                  <SelectTrigger className="h-9" data-testid={`${testid}-frequency`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekdays">Weekdays only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px]">Send around</Label>
                <Input type="time" value={prefs.delivery_time || "08:00"} onChange={(e) => setPrefs({ ...prefs, delivery_time: e.target.value })}
                  onBlur={(e) => save({ delivery_time: e.target.value })} className="h-9" data-testid={`${testid}-time`} />
              </div>
            </div>
            <p className="text-[11.5px] text-muted-foreground pt-1 border-t border-[#EEF2F5]">We only email when something actually needs your attention — never an empty reminder.</p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
